import { gql } from '@apollo/client';

const ENDPOINT = {
    ENROLL_BIOMETRICS: gql`
        mutation enrollBiometrics(
            $deviceId: String!
            $credentials: Credentials
            $biometricsProvider: BiometricsProviderType!
            $appleIdentifier: String
        ) {
            enrollBiometrics(
                deviceId: $deviceId
                credentials: $credentials
                biometricsProvider: $biometricsProvider
                appleIdentifier: $appleIdentifier
            ) {
                status
            }
        }
    `,
    GET_BIOMETRIC_REGISTRATION_OPTIONS: gql`
        query getBiometricRegistrationOptions($deviceId: String!) {
            getBiometricRegistrationOptions(deviceId: $deviceId) {
                rp {
                    name
                    id
                    icon
                }
                user {
                    username
                    id {
                        type
                        data
                    }
                }
                challenge {
                    type
                    data
                }
                pubKeyCredParams {
                    type
                    alg
                }
                timeout
                attestation
                authenticatorSelection {
                    authenticatorAttachment
                    requireResidentKey
                    userVerification
                }
            }
        }
    `,
    GET_AUTHENTICATION_OPTIONS: gql`
        query getBiometricsAuthenticationOptions($credentialId: String!, $deviceId: String!) {
            getBiometricsAuthenticationOptions(credentialId: $credentialId, deviceId: $deviceId) {
                challenge {
                    type
                    data
                }
                timeout
                rpId
                userVerification
            }
        }
    `,
    BIOMETRICS_LOGIN: gql`
        mutation biometricsLogin(
            $offlineToken: String!
            $browserDeviceId: String!
            $credentials: BiometricCredentials
            $biometricsProvider: BiometricsProviderType!
            $appleIdentifier: String
            $username: String!
        ) {
            biometricsLogin(
                offlineToken: $offlineToken
                browserDeviceId: $browserDeviceId
                credentials: $credentials
                biometricsProvider: $biometricsProvider
                appleIdentifier: $appleIdentifier
                username: $username
            ) {
                accessToken
                accessTokenExpiresIn
                refreshToken
                refreshTokenExpiresIn
                statusCode
                passwordResetToken
                numberOfAuthenticationAttemptsRemaining
                isION
                isR6
                mfaForced
                offlineToken
                mfaToken
            }
        }
    `,
    UNENROLL_BIOMETRICS: gql`
        mutation unenrollBiometrics($deviceId: String!) {
            unenrollBiometrics(deviceId: $deviceId) {
                status
            }
        }
    `,
};

/**
 * @name enrollBiometrics - register biometrics details of user
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.token - access_token
 * @param {Object} Obj.credential - credential for registration
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} token - access_token
 * @param {Object} credential - credential for registration
 * @returns {deviceId} - device used for enrollment
 */
export const enrollBiometricsApi = async ({
    graphql,
    deviceId,
    credential,
    biometricsProvider,
    appleIdentifier,
}) => {
    try {
        const { data } = await graphql.mutate({
            mutation: ENDPOINT.ENROLL_BIOMETRICS,
            variables: {
                deviceId,
                credentials: credential,
                biometricsProvider,
                appleIdentifier,
            },
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name getBiometricRegistrationOptionsApi - to get the challenge for registration
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @returns {challenge} - challenge to generate the public key for registration
 */
export const getBiometricRegistrationOptionsApi = async ({ graphql, deviceId }) => {
    try {
        const data = await graphql.query({
            query: ENDPOINT.GET_BIOMETRIC_REGISTRATION_OPTIONS,
            variables: {
                deviceId,
            },
        });

        return data?.data?.getBiometricRegistrationOptions;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name getAuthenticationOptionsApi - to get the public key for authentication
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} deviceId - deviceId
 * @returns {status} - status code
 */
export const getAuthenticationOptionsApi = async ({ graphql, credentialId, deviceId }) => {
    try {
        const data = await graphql.query({
            query: ENDPOINT.GET_AUTHENTICATION_OPTIONS,
            variables: {
                credentialId,
                deviceId,
            },
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name biometricsLoginApi - to authenticate the user
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.username - emailAddress
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} username - emailAddress
 * @param {String} deviceId - deviceId
 * @returns {Promise<string>} status - status code
 */
export const biometricsLoginApi = async ({
    graphql,
    offlineToken,
    deviceId,
    credentials,
    biometricsProvider,
    appleIdentifier,
    username,
}) => {
    try {
        const data = await graphql.mutate({
            mutation: ENDPOINT.BIOMETRICS_LOGIN,
            variables: {
                offlineToken,
                browserDeviceId: deviceId,
                credentials,
                biometricsProvider,
                appleIdentifier,
                username,
            },
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};

/**
 * @name unenrollBiometricsApi - to un-register the user
 * @param {Object} Obj - attributes for the function
 * @param {GraphQL} Obj.graphql - appoloclient graphql constant
 * @param {String} Obj.deviceId - deviceId
 * @param {GraphQL} graphql - appoloclient graphql constant
 * @param {String} deviceId - deviceId
 * @returns {Promise<string>} status - status code
 */
export const unenrollBiometricsApi = async ({ graphql, deviceId }) => {
    try {
        const data = await graphql.mutate({
            mutation: ENDPOINT.UNENROLL_BIOMETRICS,
            variables: {
                deviceId,
            },
        });

        return data;
    } catch (error) {
        throw new Error(error);
    }
};
